import React from 'react';
import styled from 'styled-components';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: Record<string, unknown>
      ) => string;
      execute: (widgetId: string, options?: Record<string, unknown>) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// sitekeys are public identifiers (like the Auth0 client id); staging and
// prod have separate Turnstile widgets. local dev and tests use Cloudflare's
// always-pass test key
const SITE_KEYS: Record<string, string> = {
  production: '0x4AAAAAAD_ONvKnGAZUgsMM',
  staging: '0x4AAAAAAD_OBI4BXBMUCUA3',
};
const ALWAYS_PASS_TEST_SITE_KEY = '1x00000000000000000000AA';

export const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY ??
  SITE_KEYS[process.env.REACT_APP_ENV ?? ''] ??
  ALWAYS_PASS_TEST_SITE_KEY;

let scriptPromise: Promise<void> | null = null;
function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load Turnstile'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

// no spacing until the widget has actually appeared, so it takes up no room
// in the common case where no challenge is ever shown. once shown it keeps
// its spacing — the widget stays on screen through its success state, and
// dropping the margin at token time would make it jump into the button
const WidgetContainer = styled.div<{ $isShown: boolean }>`
  width: 100%;
  margin-top: ${({ $isShown }) => ($isShown ? '12px' : '0')};
`;

export type TurnstileHandle = {
  /**
   * Runs the challenge on demand and resolves with a single-use token.
   * Nothing is shown to the user unless Cloudflare decides a challenge is
   * needed, so callers can await this before every protected request.
   */
  execute: () => Promise<string>;
};

// Deferred Turnstile: the widget renders invisibly and does nothing until
// execute() is called, so the challenge only appears once the user has
// committed to an action — never as a checkbox gating a dead button.
const Turnstile = React.forwardRef<TurnstileHandle, { className?: string }>(
  function Turnstile({ className }, ref) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const widgetIdRef = React.useRef<string | null>(null);
    const readyRef = React.useRef<Promise<void> | null>(null);
    const pendingRef = React.useRef<{
      resolve: (token: string) => void;
      reject: (error: Error) => void;
    } | null>(null);
    const [isShown, setIsShown] = React.useState(false);

    const settle = React.useCallback(
      (outcome: { token?: string; error?: Error }) => {
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (!pending) {
          return;
        }
        if (outcome.token) {
          pending.resolve(outcome.token);
        } else {
          pending.reject(outcome.error ?? new Error('Verification failed'));
        }
      },
      []
    );

    React.useImperativeHandle(
      ref,
      () => ({
        execute: async () => {
          await readyRef.current;
          const widgetId = widgetIdRef.current;
          if (!widgetId || !window.turnstile) {
            throw new Error('Turnstile is not ready');
          }
          if (pendingRef.current) {
            throw new Error('Verification already in progress');
          }
          return new Promise<string>((resolve, reject) => {
            pendingRef.current = { resolve, reject };
            setIsShown(true);
            // tokens are single-use, so always start from a clean widget
            window.turnstile?.reset(widgetId);
            window.turnstile?.execute(widgetId);
          });
        },
      }),
      []
    );

    React.useEffect(() => {
      let isMounted = true;
      readyRef.current = loadTurnstileScript()
        .then(() => {
          if (!isMounted || !containerRef.current || !window.turnstile) {
            return;
          }
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            // the auth screens are dark regardless of OS preference, so pin
            // the widget theme rather than letting Cloudflare auto-detect
            theme: 'dark',
            size: 'flexible',
            // don't run — or show — the challenge until execute() is called
            execution: 'execute',
            appearance: 'execute',
            callback: (token: string) => settle({ token }),
            'error-callback': () =>
              settle({ error: new Error('Verification failed') }),
            'timeout-callback': () =>
              settle({ error: new Error('Verification timed out') }),
            'expired-callback': () =>
              settle({ error: new Error('Verification expired') }),
          });
        })
        .catch(() => {
          settle({ error: new Error('Turnstile failed to load') });
        });
      return () => {
        isMounted = false;
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, [settle]);

    return (
      <WidgetContainer
        ref={containerRef}
        className={className}
        $isShown={isShown}
      />
    );
  }
);

export default Turnstile;
